import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { PatProvider, UsersService } from '../../services/users.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { UtcDatePipe } from '../../pipes/utc-date.pipe';

export interface PatUpdateDialogData {
  title?: string;
  intro?: string;
  provider?: PatProvider;
}

export interface PatUpdateDialogResult {
  proceed: boolean;
}

@Component({
  selector: 'app-pat-update-dialog',
  templateUrl: './pat-update-dialog.component.html',
  styleUrls: ['./pat-update-dialog.component.css'],
  imports: [CommonModule, FormsModule, MaterialModule, SpinnerComponent, UtcDatePipe]
})
export class PatUpdateDialogComponent implements OnInit {
  patStatus: 'loading' | 'missing' | 'configured' = 'loading';
  patExpiresAt: string | null = null;
  showUpdatePat = false;

  patInput = '';
  showPat = false;
  isSavingPat = false;
  patSaveError: string | null = null;

  patExpiresAtInput = '';
  patExpiryPreset: '' | '30' | '60' | '90' | 'custom' = '';

  constructor(
    public dialogRef: MatDialogRef<PatUpdateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PatUpdateDialogData,
    private usersService: UsersService
  ) { }

  ngOnInit(): void {
    this.checkPatStatus();
  }

  get provider(): PatProvider {
    return this.data.provider ?? 'azure-devops';
  }

  get providerLabel(): string {
    return this.provider === 'github' ? 'GitHub Enterprise' : 'Azure DevOps';
  }

  private checkPatStatus(): void {
    this.usersService.getPatStatus(this.provider).subscribe({
      next: (status) => {
        this.patStatus = status.hasPatConfigured ? 'configured' : 'missing';
        this.patExpiresAt = status.expiresAt;

        if (this.patStatus === 'missing' && !this.patExpiresAtInput) {
          this.patExpiryPreset = '30';
          this.onPatExpiryPresetChange();
        }
      },
      error: () => {
        this.patStatus = 'missing';
        if (!this.patExpiresAtInput) {
          this.patExpiryPreset = '30';
          this.onPatExpiryPresetChange();
        }
      }
    });
  }

  savePat(): void {
    if (!this.patInput.trim() || this.isSavingPat) return;

    this.isSavingPat = true;
    this.patSaveError = null;

    const expiresAt = this.resolvePatExpiresAt();
    this.usersService.saveUserPat(this.patInput.trim(), expiresAt, this.provider).subscribe({
      next: () => {
        this.isSavingPat = false;
        this.patInput = '';
        this.showPat = false;
        this.patExpiresAt = expiresAt;
        this.patStatus = 'configured';
        this.showUpdatePat = false;
      },
      error: (err) => {
        this.isSavingPat = false;
        this.patSaveError = err.error?.message || 'Failed to save PAT. Please try again.';
      }
    });
  }

  openUpdatePat(): void {
    this.showUpdatePat = true;
    this.patSaveError = null;
    this.patInput = '';
    this.showPat = false;
    this.patExpiresAtInput = this.patExpiresAt
      ? new Date(this.patExpiresAt).toISOString().substring(0, 10)
      : '';
    this.patExpiryPreset = this.patExpiresAtInput ? 'custom' : '';
  }

  onPatExpiryPresetChange(): void {
    if (!this.patExpiryPreset) {
      this.patExpiresAtInput = '';
      return;
    }

    if (this.patExpiryPreset === 'custom') {
      return;
    }

    const days = Number(this.patExpiryPreset);
    if (!Number.isFinite(days) || days <= 0) {
      this.patExpiresAtInput = '';
      return;
    }

    const date = new Date();
    date.setUTCDate(date.getUTCDate() + days);
    this.patExpiresAtInput = date.toISOString().substring(0, 10);
  }

  private resolvePatExpiresAt(): string | null {
    if (!this.patExpiresAtInput) return null;
    return new Date(this.patExpiresAtInput).toISOString();
  }

  get patExpiryWarning(): 'expired' | 'expiring-soon' | null {
    if (!this.patExpiresAt) return null;
    const now = new Date();
    const expires = new Date(this.patExpiresAt);
    if (expires <= now) return 'expired';

    const msIn30Days = 30 * 24 * 60 * 60 * 1000;
    if (expires.getTime() - now.getTime() <= msIn30Days) return 'expiring-soon';
    return null;
  }

  canProceed(): boolean {
    return this.patStatus === 'configured' && !this.showUpdatePat;
  }

  onCancel(): void {
    this.dialogRef.close({ proceed: false } as PatUpdateDialogResult);
  }

  onContinue(): void {
    if (!this.canProceed()) return;
    this.dialogRef.close({ proceed: true } as PatUpdateDialogResult);
  }
}
